import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Globe, Phone, Mail, Instagram } from 'lucide-react';
import { useCompanyName } from '../../../shared/useCompanyName';

// Closing "Keep In Touch" card — last thing printed on both Pricelist
// reports, after every category's table. Deliberately placed at the end
// rather than beside page 1's About Us block: a product's worth of page-1
// height is a real cost on a printed pricelist (rows.length risk
// discussed when this was designed), so this card lives where it can have
// proper structure — heading, QR, icon rows — without competing with the
// product table for room. Renders nothing when Instagram/Website/Phone/
// Email are all blank, so a tenant that hasn't filled any in sees no gap.
const CARD_STYLE = {
  breakInside: 'avoid',
  pageBreakInside: 'avoid',
  marginTop: 8,
  width: 260,
  border: '1px solid #dde4ec',
  borderRadius: 8,
  background: '#f8fafc',
  padding: 16,
};
const HEADING_STYLE = { fontSize: 13, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: '#3a6ea5', margin: '0 0 12px', textAlign: 'center' };
const ROW_STYLE = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#444', marginTop: 8 };

// The QR code encodes the full URL; the handle read out from it is what a
// human sees next to it, e.g. "https://instagram.com/kronig_supply" -> "@kronig_supply".
function instagramHandle(url) {
  const match = url.match(/instagram\.com\/([^/?#]+)/i);
  return match ? `@${match[1]}` : url;
}

// `companyInfo` — see useCompanyName.js — threaded through for the
// server-rendered PDF attachment path (issue #238).
function PricelistContactBlock({ companyInfo }) {
  const { instagramUrl, website, phoneDomestic, phoneIntl, email } = useCompanyName(companyInfo);
  const hasInstagram = instagramUrl.trim().length > 0;
  const hasWebsite = website.trim().length > 0;
  const hasPhone = phoneDomestic.trim().length > 0;
  const hasEmail = email.trim().length > 0;
  if (!hasInstagram && !hasWebsite && !hasPhone && !hasEmail) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <div style={CARD_STYLE}>
        <h3 style={HEADING_STYLE}>Keep In Touch</h3>
        {hasInstagram && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: hasWebsite || hasPhone || hasEmail ? 12 : 0 }}>
            <QRCodeSVG value={instagramUrl} size={92} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 6 }}>
              <Instagram size={13} color="#3a6ea5" style={{ flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: '#444', fontWeight: 600 }}>{instagramHandle(instagramUrl)}</span>
            </div>
          </div>
        )}
        {hasWebsite && (
          <div style={ROW_STYLE}>
            <Globe size={13} color="#3a6ea5" style={{ flexShrink: 0 }} />
            <span style={{ wordBreak: 'break-word' }}>{website}</span>
          </div>
        )}
        {hasPhone && (
          <div style={{ ...ROW_STYLE, alignItems: 'flex-start' }}>
            <Phone size={13} color="#3a6ea5" style={{ flexShrink: 0, marginTop: 2 }} />
            <div>
              {/* Domestic and international dial forms shown together —
                  some customers call from within Australia, some from
                  outside, and no single notation reads correctly and
                  literally-dialable for both (see useCompanyName.js). */}
              <div>{phoneDomestic}</div>
              {phoneIntl && <div style={{ color: '#888', fontSize: 11 }}>{phoneIntl}</div>}
            </div>
          </div>
        )}
        {hasEmail && (
          <div style={ROW_STYLE}>
            <Mail size={13} color="#3a6ea5" style={{ flexShrink: 0 }} />
            <span style={{ wordBreak: 'break-word' }}>{email}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default PricelistContactBlock;
